/**
 * 服务类型的展示配色：**按名字哈希取色号，不按名字表取色**。
 *
 * 库里 `orders.service_type` 存的是**名称**（VARCHAR(50)，无外键），目录又按创建人私有化、
 * 名字由用户自己填，还会随「其他用户」交互进来的订单直接落在我这张单上。原先三处展示
 * （订单卡片 chip、列表筛选条、服务类型收入分布）共用一张
 * `会议拍摄/活动拍摄/照片直播/直播服务` 的写死表，而这四个名字有三个在系统里根本不存在，
 * 连后端预设的 `拍摄服务`、`场地布置` 都不在表内——实测当前库在用的 10 个类型名只有 1 个命中，
 * 其余九成全落兜底灰，「不同类型不同颜色」名存实亡。
 * 写死表在一个可自由增名的开放集合上永远封闭不了，所以取色不再问「这是什么类型」，
 * 只对名字做哈希：同名恒同色（卡片、筛选条、任何一处都对得上），新类型自动有色，无需维护清单。
 * 色号有限，**不同名字撞成同色是预期的**——chip 上永远印着名字本身，颜色只是扫读辅助，
 * 不承担身份；要「一个类型一个颜色且互不相同」只能给目录表加一列由用户自己挑。
 */

/**
 * chip 配色：浅底 + 深字 + 同族描边，保证白卡上的对比度与既有徽章一致。
 * 类名必须是**整串字面量**——Tailwind 只扫源码里出现过的原子类，运行时拼出来的名字不会进产物。
 */
const CHIP_CLASSES = [
  'bg-teal-50 text-teal-600 border border-teal-200',
  'bg-amber-50 text-amber-600 border border-amber-200',
  'bg-sky-50 text-sky-600 border border-sky-200',
  'bg-rose-50 text-rose-600 border border-rose-200',
  'bg-violet-50 text-violet-600 border border-violet-200',
  'bg-orange-50 text-orange-600 border border-orange-200',
  'bg-cyan-50 text-cyan-600 border border-cyan-200',
  'bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-200',
  'bg-lime-50 text-lime-700 border border-lime-200',
  'bg-slate-50 text-slate-600 border border-slate-200',
];

/**
 * 服务类型图标只有这一个：图标表达的是「这是哪种服务」，而名字是用户自由文本，
 * 从字符串里读不出摄像机还是舞台灯。原先按名字配的四支图标同样只有 1/10 命中。
 * 真要每类一个图标，得给目录表加一列由用户选，那是功能不是配色。
 */
export const SERVICE_TYPE_ICON = 'ri-camera-3-line';

/**
 * 分布图配色：按**行序**轮转，与 chip 的「同名恒色」是两种用途——
 * 这张卡要的是相邻两行必然不同色（哈希在 8~10 个色号下会让两行撞色，
 * 「分布」又退回只能靠文字区分）；chip 要的是同一个类型在任何位置都是同一个颜色。
 */
export const CHART_PALETTE: { bar: string; text: string }[] = [
  { bar: 'bg-teal-400', text: 'text-teal-600' },
  { bar: 'bg-amber-400', text: 'text-amber-600' },
  { bar: 'bg-violet-500', text: 'text-violet-600' },
  { bar: 'bg-sky-400', text: 'text-sky-600' },
  { bar: 'bg-rose-400', text: 'text-rose-600' },
  { bar: 'bg-emerald-500', text: 'text-emerald-600' },
  { bar: 'bg-orange-400', text: 'text-orange-600' },
  { bar: 'bg-fuchsia-500', text: 'text-fuchsia-600' },
];

/**
 * FNV-1a + 末尾混淆。
 * 别用 `h*31+c`：31 对色号数 10 同余于 1，滚动哈希退化成「码点求和取模」，
 * 实测「摄影服务 / 拍摄服务 / 摄像」三个名字撞进同一支橙——色号白给了。
 */
function hueIndexOf(name: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < name.length; i += 1) {
    h ^= name.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  h ^= h >>> 13;
  return (h >>> 0) % CHIP_CLASSES.length;
}

/** 订单卡片与筛选条上的服务类型 chip；名字为空时取第一个色号（调用方应在为空时不渲染）。 */
export function serviceTypeChipClass(name: string): string {
  return CHIP_CLASSES[hueIndexOf(name.trim())];
}
